import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { Check, Compass } from 'lucide-react';
import { Abas, PainelAba } from '../componentes/abas';
import { Botao, Cartao, Selo } from '../componentes/base';
import { FormPreferenciasDiscreto } from '../componentes/discreto';
import { ListaLixeira } from '../componentes/lixeira';
import { Vazio } from '../componentes/estados';
import { rolarAbaAtiva } from '../nucleo/foco';
import { Entrada, Rotulo, Selecao } from '../componentes/formulario';
import { anunciarOcultos } from '../componentes/sensivel';
import { TextoCortado } from '../componentes/texto-cortado';
import { SubtituloPagina } from '../componentes/subtitulo-pagina';
import { useToast } from '../componentes/toast';
import { get, mensagemDeErro, patch } from '../nucleo/api';
import { ESCALAS, fonteEfetiva, FONTES_DE_ESTILO, FONTES_PREVIAS, registrarFonte, resolverFonte, pilhaFonte, type EscalaTexto } from '../nucleo/fontes';
import { aplicarEstilo, ehEstilo, ESTILO_PADRAO, FONTE_DO_ESTILO, type Estilo } from '../nucleo/estilos';
import { estiloEfetivo } from '@malgoos/shared';
import { OpcaoRadio, SeletorEstilo } from '../componentes/seletor-estilo';
import { email, type Idioma } from '../nucleo/formato';
import { rotuloAcao, rotuloModulo } from '../nucleo/rotulos';
import { useSessao } from '../nucleo/sessao';
import { useSubsistema } from '../nucleo/subsistemas';
import { ArvoreAcesso } from '../modules/admin/ArvoreAcesso';
import { chavesDoRotuloDaAcao } from '../modules/admin/arvore-acesso';
import type { Permissao } from '../modules/admin/tipos';
import { PERMISSOES_SUBSISTEMAS } from '@malgoos/shared';
import { moduloWeb } from '../nucleo/modulos-web';
import { CartaoAcessoConta, CartaoSegundoFator, CartaoSenha, CartaoSessoes } from './conta-seguranca';
import { AbaAssistentes } from './conta-assistentes';
import { CartaoPasskeys } from './conta-passkeys';
import { AvatarUsuario } from '../componentes/avatar-usuario';
import { CartaoEntrada, FotoPerfilAcoes, useMeusPedidos } from './conta-entrada';
import { useIcones } from '../nucleo/icones-navegacao';

/**
 * Minha conta (r9 spec B5; fase 2, W4 e decisão D23): uma página só, com as abas Perfil, Aparência, Segurança e as demais no
 * padrão de abas do núcleo (abas que navegam: `/conta`, `/conta/aparencia`, `/conta/seguranca`). Notificações tem rota própria
 * (W3, `/conta/notificacoes`) e, desde a lista de 30/10 (item 7), não é mais aba daqui.
 */

// Fase 2 (W11): a aba Meus assistentes (`/conta/assistentes`) mora em `conta-assistentes.tsx`.
// Fase 2 (W14): a aba Lixeira (`/conta/lixeira`) lista o que foi excluído nos últimos 30 dias nos módulos que a pessoa vê.
// "Meus dados" (21/10): a própria ficha de funcionário (`/conta/meus-dados`), publicada pelo Pessoas (`abaMinhaConta`); só
// aparece com o módulo instalado.
type ChaveAba = 'perfil' | 'meus-dados' | 'aparencia' | 'seguranca' | 'assistentes' | 'lixeira';
/** Abas de Minha conta (também no mapa de caminhos, `nucleo/caminhos.ts`). */
// Lista de 30/10 (item 7): Notificações saiu das abas (tem lugar próprio: o sino e `/conta/notificacoes`).
export const ABAS: ChaveAba[] = ['perfil', 'meus-dados', 'aparencia', 'seguranca', 'assistentes', 'lixeira'];
const ID_ABAS = 'conta';

/** Iniciais do nome (duas primeiras palavras), como o rodapé da barra lateral. */
export function iniciais(nome: string): string {
  return nome
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((p) => p[0]!)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

/** Permissões da sessão agrupadas pelos módulos instalados, na ordem do menu (função pura, testada). */
export function acessosPorModulo(permissoes: string[], modulos: { slug: string }[]): { slug: string; acoes: string[] }[] {
  const porModulo = new Map<string, string[]>();
  for (const p of permissoes) {
    const i = p.indexOf('.');
    if (i <= 0) continue;
    const slug = p.slice(0, i);
    porModulo.set(slug, [...(porModulo.get(slug) ?? []), p.slice(i + 1)]);
  }
  return modulos.filter((m) => porModulo.has(m.slug)).map((m) => ({ slug: m.slug, acoes: porModulo.get(m.slug)! }));
}

/**
 * Val3 (ficha 2026-10-29, item 2): o catálogo da árvore de "Meus acessos" a partir das permissões da sessão (só as que a pessoa
 * tem; as de uso dos sub-sistemas e as do núcleo ficam no núcleo, como no catálogo do servidor). Função pura, testada.
 */
export function catalogoDaSessao(permissoes: readonly string[], modulos: readonly { slug: string; nome?: string }[]): Permissao[] {
  return permissoes
    .filter((c) => c.includes('.'))
    .map((codigo) => {
      const i = codigo.indexOf('.');
      const prefixo = codigo.slice(0, i);
      const nucleo = prefixo === 'nucleo' || PERMISSOES_SUBSISTEMAS.includes(codigo);
      const modulo = nucleo ? 'nucleo' : prefixo;
      return { codigo, modulo, modulo_nome: nucleo ? '' : (modulos.find((m) => m.slug === modulo)?.nome ?? modulo), acao: codigo.slice(i + 1), descricao: '' };
    });
}

export function MinhaConta() {
  const { t } = useTranslation();
  // D2: ícones nas abas internas (ligados por padrão; só o desligar explícito do workspace tira).
  const icones = useIcones();
  const params = useParams();
  const sessao = useSessao();
  const MeusDados = sessao.instalado('pessoas') ? moduloWeb('pessoas')?.abaMinhaConta : undefined;
  const abas = ABAS.filter((a) => a !== 'meus-dados' || !!MeusDados);
  const sub = (params['*'] ?? '').split('/')[0] as ChaveAba | '';
  const ativa: ChaveAba = abas.includes(sub as ChaveAba) ? (sub as ChaveAba) : 'perfil';
  // No celular a faixa de abas rola na horizontal: a aba ativa (Segurança, a última) entra na vista.
  useEffect(() => {
    // Review r34 visual B1: sem `scrollIntoView` (movia o ponto de partida do Tab para a aba).
    const aba = document.querySelector<HTMLElement>(`[data-abas="conta"] [role="tab"][aria-selected="true"]`);
    if (aba) rolarAbaAtiva(aba, 'perto');
  }, [ativa]);
  return (
    <div className="flex flex-col gap-6">
      <header className="min-w-0">
        <p className="kicker">{t('conta.kicker')}</p>
        <TextoCortado como="h1" className="mt-1 text-2xl font-bold tracking-tight">
          {t('conta.titulo')}
        </TextoCortado>
        <SubtituloPagina caminho={ativa === 'perfil' ? '/conta' : `/conta/${ativa}`} />
      </header>
      <Abas<ChaveAba>
        idBase={ID_ABAS}
        grupo="conta"
        rotulo={t('conta.rotuloAbas')}
        ativa={ativa}
        itens={abas.map((a) => ({ chave: a, rotulo: a === 'assistentes' ? t('assistentes.aba') : a === 'lixeira' ? t('lixeira.aba') : t(`conta.abas.${a}`), para: a === 'perfil' ? '/conta' : `/conta/${a}`, icone: icones.de(`conta.${a}`) }))}
      />
      <PainelAba idBase={ID_ABAS} chave={ativa} className="min-w-0">
        {ativa === 'perfil' && <AbaPerfil />}
        {ativa === 'meus-dados' && MeusDados && <MeusDados />}
        {ativa === 'aparencia' && <AbaAparenciaPessoal />}
        {ativa === 'seguranca' && <AbaSeguranca />}
        {ativa === 'assistentes' && <AbaAssistentes />}
        {ativa === 'lixeira' && <AbaLixeira />}
      </PainelAba>
    </div>
  );
}

/** Fase 2 (W14): a lixeira de 30 dias de todos os módulos que a pessoa vê, com "Desfazer exclusão" onde ela pode excluir. */
function AbaLixeira() {
  const { t } = useTranslation();
  return (
    <Cartao titulo={t('lixeira.titulo')}>
      <ListaLixeira texto={t('lixeira.textoGeral')} filtrosNaUrl />
    </Cartao>
  );
}

// ---------------------------------------------------------------------------------------------------------------------
// Perfil

function AbaPerfil() {
  const sessao = useSessao();
  // Lista 30/10 B (item 34): cartões dos módulos instalados (Agenda: o Google Agenda da própria pessoa).
  const GoogleAgenda = sessao.instalado('agenda') ? moduloWeb('agenda')?.cartaoMinhaConta : undefined;
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <CartaoPerfil />
      <CartaoEntrada />
      <CartaoTour />
      {GoogleAgenda && <GoogleAgenda />}
      <CartaoAcessos />
    </div>
  );
}

function CartaoPerfil() {
  const { t } = useTranslation();
  const s = useSessao();
  const toast = useToast();
  const [nome, setNome] = useState(s.usuario.nome);
  const [erro, setErro] = useState<string | null>(null);
  const salvar = useMutation({
    mutationFn: () => patch('/conta/preferencias', { nome }),
    onSuccess: async () => {
      await s.recarregar();
      toast.sucesso(t('conta.nomeSalvo'));
    },
    onError: (e) => setErro(mensagemDeErro(e)),
  });
  const mudou = nome.trim() !== s.usuario.nome;
  function enviar(e: FormEvent) {
    e.preventDefault();
    setErro(null);
    if (!nome.trim()) return setErro(t('conta.nomeVazio'));
    salvar.mutate();
  }
  return (
    <Cartao titulo={t('conta.perfil')}>
      <form onSubmit={enviar} noValidate className="flex flex-col gap-4" data-conta-perfil>
        <div className="flex min-w-0 items-center gap-3">
          {/* O próprio usuário do sistema fica com o nome (decisão r9; r10 visual B6). */}
          <AvatarUsuario role="img" aria-label={t('conta.iniciais', { nome: s.usuario.nome })} usuarioId={s.usuario.id} versao={s.usuario.avatar_versao} tamanho={256} className="flex size-12 shrink-0 items-center justify-center rounded-full bg-sup-3 text-base font-semibold">
            {iniciais(s.usuario.nome)}
          </AvatarUsuario>
          <div className="min-w-0">
            <TextoCortado className="text-sm font-medium text-tinta-1">{s.usuario.nome}</TextoCortado>
            <p className="numero text-xs break-all text-tinta-3">{anunciarOcultos(email(s.usuario.email))}</p>
          </div>
        </div>
        <FotoPerfilAcoes />
        <Rotulo id="conta-nome" rotulo={t('conta.nome')} erro={erro ?? undefined}>
          <Entrada id="conta-nome" autoComplete="name" maxLength={120} value={nome} aria-invalid={!!erro || undefined} aria-describedby={erro ? 'conta-nome-erro' : undefined} onChange={(e) => setNome(e.target.value)} />
        </Rotulo>
        <div className="flex min-w-0 flex-col gap-1.5 text-sm">
          <span className="font-medium text-tinta-2">{t('conta.email')}</span>
          <span className="numero break-all text-tinta-1">{anunciarOcultos(email(s.usuario.email))}</span>
        </div>
        <div className="flex justify-end">
          <Botao type="submit" variante="primario" disabled={!mudou} carregando={salvar.isPending}>
            {t('conta.salvarNome')}
          </Botao>
        </div>
      </form>
    </Cartao>
  );
}

function CartaoTour() {
  const { t } = useTranslation();
  const s = useSessao();
  const navegar = useNavigate();
  return (
    <Cartao titulo={t('conta.tour.titulo')}>
      <div className="flex flex-col gap-4">
        <p className="text-sm text-tinta-3">{t('conta.tour.texto')}</p>
        <div className="flex justify-end">
          <Botao
            icone={<Compass className="size-4" aria-hidden />}
            data-rever-tour
            onClick={async () => {
              await s.preferencias({ onboarding_concluido: false });
              navegar('/');
            }}
          >
            {t('conta.tour.botao')}
          </Botao>
        </div>
      </div>
    </Cartao>
  );
}

function CartaoAcessos() {
  const { t } = useTranslation();
  const { t: ta } = useTranslation('admin');
  const s = useSessao();
  const sub = useSubsistema();
  const grupos = useMemo(() => acessosPorModulo(s.permissoes, s.modulos), [s.permissoes, s.modulos]);
  const catalogo = useMemo(() => catalogoDaSessao(s.permissoes, s.modulos), [s.permissoes, s.modulos]);
  const estado = useMemo(() => ({ permissoes: new Set(s.permissoes), negadas: new Set(s.paginas_negadas ?? []) }), [s.permissoes, s.paginas_negadas]);
  const nomeModulo = (slug: string) => t(`${slug}:nome`, { defaultValue: rotuloModulo(slug, s.modulos.find((x) => x.slug === slug)?.nome) });
  return (
    <Cartao titulo={t('conta.acessos.titulo')} className="lg:col-span-2" acao={grupos.length > 0 && <Selo>{t('conta.acessos.modulos', { count: grupos.length })}</Selo>}>
      <div className="flex flex-col gap-3" data-meus-acessos>
        <p className="text-sm text-tinta-3">{t(sub.ativo ? 'conta.acessos.textoSubsistemas' : 'conta.acessos.texto')}</p>
        {grupos.length === 0 ? (
          <Vazio titulo={t('conta.acessos.titulo')} texto={t('conta.acessos.vazio')} />
        ) : sub.ativo ? (
          // Com sub-sistemas ligados, o mesmo recorte da árvore do papel: só os sub-sistemas que a pessoa usa e as páginas que abre.
          <ArvoreAcesso permissoes={catalogo} estado={estado} somenteLeitura soMarcados sujeito="voce" />
        ) : (
          <ul className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {grupos.map((g) => (
              <li key={g.slug} className="min-w-0 rounded-lg border border-borda p-3">
                <p className="text-sm font-semibold text-tinta-1">{nomeModulo(g.slug)}</p>
                <ul className="mt-2 flex flex-wrap gap-1.5">
                  {g.acoes.map((a) => (
                    <li key={a}>
                      <Selo quebra>
                        <Check className="mr-1 size-3 text-verde" aria-hidden />
                        {ta(chavesDoRotuloDaAcao(`${g.slug}.${a}`, a), { defaultValue: rotuloAcao(a) })}
                      </Selo>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Cartao>
  );
}

// ---------------------------------------------------------------------------------------------------------------------
// Aparência pessoal: tema, idioma, estilo, fonte (se o admin permitir), escala de texto e modo discreto

function AbaAparenciaPessoal() {
  const { t } = useTranslation();
  const s = useSessao();
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Cartao titulo={t('conta.aparencia')}>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex min-w-0 flex-col gap-1.5 text-sm">
            <label htmlFor="conta-tema" className="font-medium text-tinta-1">
              {t('comum.tema')}
            </label>
            <Selecao
              id="conta-tema"
              value={s.usuario.tema}
              onChange={(e) => void s.preferencias({ tema: e.target.value as 'escuro' | 'claro' })}
              opcoes={[
                { valor: 'escuro', rotulo: t('comum.temaEscuro') },
                { valor: 'claro', rotulo: t('comum.temaClaro') },
              ]}
            />
          </div>
          <div className="flex min-w-0 flex-col gap-1.5 text-sm">
            <label htmlFor="conta-idioma" className="font-medium text-tinta-1">
              {t('comum.idioma')}
            </label>
            <Selecao
              id="conta-idioma"
              value={s.usuario.idioma}
              onChange={(e) => void s.preferencias({ idioma: e.target.value as Idioma })}
              opcoes={[
                { valor: 'pt', rotulo: 'Português' },
                { valor: 'en', rotulo: 'English' },
                { valor: 'es', rotulo: 'Español' },
              ]}
            />
          </div>
        </div>
      </Cartao>
      <CartaoEscala />
      <CartaoEstilo />
      <CartaoModoSimples />
      <CartaoFontePessoal />
      <Cartao titulo={t('discreto.secao')}>
        <div data-conta-discreto>
          <FormPreferenciasDiscreto prefixo="conta-discreto" comModo />
        </div>
      </Cartao>
    </div>
  );
}

/**
 * Estilos de interface (27/10; N5, 28/10): a Malgo (padrão) e os nove inspirados em design systems públicos. A pessoa escolhe o
 * dela, ou "Padrão do workspace" (null); o administrador pode desligar a escolha pessoal ou fixar o estilo de uma pessoa, e nos
 * dois casos o cartão fica só leitura explicando. A troca vale na hora; se o servidor recusar, volta ao salvo.
 */
function CartaoEstilo() {
  const { t } = useTranslation();
  const s = useSessao();
  const toast = useToast();
  const fixado = ehEstilo(s.usuario.estilo_fixado) ? s.usuario.estilo_fixado : null;
  const permitido = s.aparencia?.permitir_estilo_pessoal !== false;
  const padraoWs: Estilo = ehEstilo(s.aparencia?.estilo_padrao) ? s.aparencia.estilo_padrao : ESTILO_PADRAO;
  const pessoa: Estilo | null = ehEstilo(s.usuario.estilo) ? s.usuario.estilo : null;
  const efetivo = estiloEfetivo({ fixado, pessoa, permitirPessoal: permitido, padrao: padraoWs }) as Estilo;
  const travado = !!fixado || !permitido;
  const salvar = useMutation({
    mutationFn: (estilo: Estilo | null) => patch('/conta/preferencias', { estilo }),
    onMutate: (estilo) => aplicarEstilo(estilo ?? padraoWs),
    onSuccess: async () => {
      await s.recarregar();
      toast.sucesso(t('conta.estilo.salvo'));
    },
    onError: (e) => {
      aplicarEstilo(efetivo);
      toast.erro(mensagemDeErro(e));
    },
  });
  return (
    <Cartao titulo={t('conta.estilo.titulo')} className="lg:col-span-2">
      <div className="flex flex-col gap-3">
        <p className="text-sm text-tinta-3">{t('conta.estilo.texto')}</p>
        {travado && (
          <p className="rounded-lg border border-borda px-3 py-2 text-sm text-tinta-2" data-estilo-travado={fixado ? 'fixado' : 'workspace'}>
            {fixado ? t('conta.estilo.fixado', { nome: t(`conta.estilo.nomes.${fixado}`) }) : t('conta.estilo.bloqueado', { nome: t(`conta.estilo.nomes.${padraoWs}`) })}
          </p>
        )}
        {travado ? (
          <SeletorEstilo valor={efetivo} aoEscolher={() => undefined} desabilitado rotulo={t('conta.estilo.titulo')} />
        ) : (
          <SeletorEstilo valor={pessoa} aoEscolher={(e) => salvar.mutate(e)} desabilitado={salvar.isPending} rotulo={t('conta.estilo.titulo')} padrao={padraoWs} rotuloPadrao={t('conta.estilo.seguirPadrao')} />
        )}
      </div>
    </Cartao>
  );
}

function CartaoEscala() {
  const { t } = useTranslation();
  const s = useSessao();
  const toast = useToast();
  const atual = (s.usuario.escala_texto ?? 100) as EscalaTexto;
  const salvar = useMutation({
    mutationFn: (escala: EscalaTexto) => patch('/conta/preferencias', { escala_texto: escala }),
    onSuccess: async () => {
      await s.recarregar();
      toast.sucesso(t('conta.fonte.salva'));
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  return (
    <Cartao titulo={t('conta.fonte.escala')}>
      <div className="flex flex-col gap-3">
        <p className="text-sm text-tinta-3">{t('conta.fonte.escalaAjuda')}</p>
        <div role="radiogroup" aria-label={t('conta.fonte.escala')} className="grid grid-cols-3 gap-2 sm:grid-cols-5" data-escala-texto>
          {ESCALAS.map((e) => (
            <OpcaoRadio key={e} marcado={atual === e} desabilitado={salvar.isPending} aoEscolher={() => e !== atual && salvar.mutate(e)}>
              <span className="numero">{e}%</span>
            </OpcaoRadio>
          ))}
        </div>
      </div>
    </Cartao>
  );
}

/** U15 F5: modo simples da própria pessoa (desligado por padrão): menos abas e campos avançados. O mesmo controle da escala. */
function CartaoModoSimples() {
  const { t } = useTranslation();
  const s = useSessao();
  const toast = useToast();
  const ligado = !!s.usuario.modo_simples;
  const salvar = useMutation({
    mutationFn: (v: boolean) => patch('/conta/preferencias', { modo_simples: v }),
    onSuccess: async () => {
      await s.recarregar();
      toast.sucesso(t('conta.fonte.salva'));
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  return (
    <Cartao titulo={t('conta.simples.titulo')}>
      <div className="flex flex-col gap-3">
        <p className="text-sm text-tinta-3">{t('conta.simples.ajuda')}</p>
        <div role="radiogroup" aria-label={t('conta.simples.titulo')} className="grid grid-cols-2 gap-2" data-modo-simples>
          {([false, true] as const).map((v) => (
            <OpcaoRadio key={String(v)} marcado={ligado === v} desabilitado={salvar.isPending} aoEscolher={() => v !== ligado && salvar.mutate(v)}>
              <span>{v ? t('conta.simples.ligado') : t('conta.simples.desligado')}</span>
            </OpcaoRadio>
          ))}
        </div>
      </div>
    </Cartao>
  );
}

interface FonteEnviada {
  id: string;
  sha256: string;
  nome: string;
  familia: string;
}

function CartaoFontePessoal() {
  const { t } = useTranslation();
  const s = useSessao();
  const toast = useToast();
  const permitido = s.aparencia?.permitir_fonte_pessoal !== false;
  const enviadas = useQuery({ queryKey: ['configuracoes', 'aparencia', 'arquivos', 'fonte'], queryFn: () => get<{ itens: FonteEnviada[] }>('/configuracoes/aparencia/arquivos', { tipo: 'fonte' }), enabled: permitido });
  // Sem fonte pessoal vale a mesma precedência da sessão: a do estilo (fora da Malgo) e depois a do workspace (review res1 VIS-B1).
  const efetivoEstilo = estiloEfetivo({ fixado: s.usuario.estilo_fixado, pessoa: s.usuario.estilo, permitirPessoal: s.aparencia?.permitir_estilo_pessoal, padrao: s.aparencia?.estilo_padrao });
  const estilo = efetivoEstilo !== 'malgo' ? efetivoEstilo : null;
  const fonteDoEstilo = estilo ? (FONTE_DO_ESTILO[estilo] ?? null) : null;
  const doWorkspace = fonteEfetiva(s.aparencia, null, fonteDoEstilo);
  const nomeDe = (chave: string) => {
    if (chave.startsWith('arquivo:')) return enviadas.data?.itens.find((f) => `arquivo:${f.sha256}` === chave)?.nome ?? t('conta.fonte.enviada');
    const p = [...FONTES_PREVIAS, ...FONTES_DE_ESTILO].find((f) => f.chave === chave);
    return p?.chave === 'sistema' ? t('conta.fonte.sistema') : (p?.nome ?? chave);
  };
  const opcoes = [...FONTES_PREVIAS.map((f) => f.chave as string), ...(enviadas.data?.itens ?? []).map((f) => `arquivo:${f.sha256}`)];
  // Prévia: cada opção aparece na própria fonte (o arquivo só baixa quando a lista é mostrada).
  const chaves = opcoes.join('|');
  useEffect(() => {
    if (permitido) for (const o of chaves.split('|')) void registrarFonte(resolverFonte(o));
  }, [permitido, chaves]);
  const salvar = useMutation({
    mutationFn: (fonte: string | null) => patch('/conta/preferencias', { fonte }),
    onSuccess: async () => {
      await s.recarregar();
      toast.sucesso(t('conta.fonte.salva'));
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const atual = s.usuario.fonte ?? null;
  return (
    <Cartao titulo={t('conta.fonte.titulo')} className="lg:col-span-2">
      <div className="flex flex-col gap-3" data-fonte-pessoal>
        <p className="text-sm text-tinta-3">{t('conta.fonte.texto')}</p>
        {!permitido ? (
          <p className="rounded-lg border border-borda px-3 py-2 text-sm text-tinta-2">{t('conta.fonte.bloqueada')}</p>
        ) : (
          <div role="radiogroup" aria-label={t('conta.fonte.titulo')} className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
            <OpcaoRadio marcado={atual === null} desabilitado={salvar.isPending} aoEscolher={() => atual !== null && salvar.mutate(null)} estilo={{ fontFamily: pilhaFonte(resolverFonte(doWorkspace).familia) }}>
              <span className="flex min-w-0 flex-col">
                <span className="font-medium">{estilo ? t('conta.fonte.doEstiloNome', { nome: nomeDe(doWorkspace) }) : t('conta.fonte.doWorkspaceNome', { nome: nomeDe(doWorkspace) })}</span>
                <span className="text-xs text-tinta-3">{t('conta.fonte.amostra')}</span>
              </span>
            </OpcaoRadio>
            {opcoes.map((o) => (
              <OpcaoRadio key={o} marcado={atual === o} desabilitado={salvar.isPending} aoEscolher={() => atual !== o && salvar.mutate(o)} estilo={{ fontFamily: pilhaFonte(resolverFonte(o).familia) }}>
                <span className="flex min-w-0 flex-col">
                  <span className="font-medium">{nomeDe(o)}</span>
                  {/* U15 F4: Atkinson e Lexend são desenhadas para leitura fácil; a frase curta as destaca no mesmo controle. */}
                  {(o === 'atkinson' || o === 'lexend') && <span className="text-xs font-medium text-tinta-2">{t('conta.fonte.facilLer')}</span>}
                  <span className="text-xs text-tinta-3">{t('conta.fonte.amostra')}</span>
                </span>
              </OpcaoRadio>
            ))}
          </div>
        )}
      </div>
    </Cartao>
  );
}

// ---------------------------------------------------------------------------------------------------------------------
// Segurança

function AbaSeguranca() {
  const [params] = useSearchParams();
  const direta = useMeusPedidos().data?.troca_direta;
  const trocaDireta = !!direta?.email || !!direta?.usuario;
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <CartaoSegundoFator abrirConfiguracao={params.get('configurar') === '1'} />
      <CartaoSenha />
      {/* val1 (28/10): nome de usuário e e-mail do login, com a troca do e-mail. Lista de 30/10 (item 11): a troca virou PEDIDO
          (Perfil > Entra com); só quem aprova pedidos (admin.usuarios) mantém aqui a troca direta, porque o próprio pedido não
          pode ser aprovado por ele mesmo. Review rl30-3 (SEG-B1): `admin.usuarios` em todo workspace da conta, pela API; e
          (SPEC-M1) a troca direta do nome de usuário, nas mesmas condições. */}
      {trocaDireta && (
        <div className="lg:col-span-2">
          <CartaoAcessoConta trocaEmail={!!direta?.email} trocaUsuario={!!direta?.usuario} />
        </div>
      )}
      {/* Fase 2 (W11): passkeys para confirmar as ações sensíveis dos assistentes. */}
      <div className="lg:col-span-2">
        <CartaoPasskeys />
      </div>
      <div className="lg:col-span-2">
        <CartaoSessoes />
      </div>
    </div>
  );
}
